import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Alert,
  Box,
  Chip,
  Paper,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  CalendarMonth,
  Event as EventIcon,
  Schedule,
  LocationOn,
  Person,
  Timer,
  DateRange,
  Groups,
  TheaterComedy,
  Headphones,           // Sound Engineer
  Business,             // Production Manager
  DirectionsRun,        // Runner
  Store,                // Front of House
  People,               // Staff
  Construction,         // Stage Hand
  RecordVoiceOver,      // Artist Liaison
  CameraAlt,            // Photographer/Videographer
  VideoSettings,        // Vision Mix Engineer
  VolunteerActivism as VolunteerIcon // Volunteer
} from '@mui/icons-material';
import { Performance, Event as EventType, Venue, BandRecommendation } from '../../types/mongodb';
import { format, differenceInDays, isSameDay } from 'date-fns';
import axiosInstance from '../../utils/axiosConfig'; // Adjust path as needed

interface MarketingAsset {
  filename: string;
  url: string;
  type: 'pdf' | 'image' | 'document';
  description?: string;
  uploadedAt: Date;
}

interface ResourceData {
  _id: string;
  marketingAssets?: MarketingAsset[];
  mainImage?: {
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
  };
  mainPosterImage?: {
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
    dimensions?: string;
    format?: string;
  };
  additionalPromoImages?: Array<{
    url: string;
    owner: 'artist' | 'imc';
    source?: string;
    description?: string;
  }>;
}

interface BandEventInfoProps {
  performance: Performance;
}

const roleIcons: Record<string, React.ReactElement> = {
  "Stage Manager": <TheaterComedy sx={{ color: '#3A9188' }} />,  
  "Sound Engineer": <Headphones sx={{ color: '#3A9188' }} />,
  "Production Manager": <Business sx={{ color: '#3A9188' }} />,
  "Runner": <DirectionsRun sx={{ color: '#3A9188' }} />,
  "Front of House": <Store sx={{ color: '#3A9188' }} />,
  "Staff": <People sx={{ color: '#3A9188' }} />,  
  "Stage Hand": <Construction sx={{ color: '#3A9188' }} />,
  "Artist Liaison": <RecordVoiceOver sx={{ color: '#3A9188' }} />,
  "Photographer/Videographer": <CameraAlt sx={{ color: '#3A9188' }} />,  
  "Vision Mix Engineer": <VideoSettings sx={{ color: '#3A9188' }} />,
  "Volunteer": <VolunteerIcon sx={{ color: '#3A9188' }} />
};

const BandEventInfo: React.FC<BandEventInfoProps> = ({ performance }) => {
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [loadingResource, setLoadingResource] = useState(false);
  const [staffDetails, setStaffDetails] = useState<Record<string, any>>({});
  const [loadingStaffDetails, setLoadingStaffDetails] = useState(false);

  // Add this useEffect to fetch staff details when needed
useEffect(() => {
  const fetchMissingStaffDetails = async () => {
    if (!performance.staff?.length) return;
    
    const staffIdsToFetch: string[] = [];
    const currentStaffDetails: Record<string, any> = {};
    
    // Check which staff need to be fetched
    performance.staff.forEach((assignment: any) => {
      const raw = assignment.staffId;
      if (typeof raw === 'string') {
        // This is just an ID, need to fetch details
        staffIdsToFetch.push(raw);
      } else if (typeof raw === 'object' && raw._id) {
        // This is already populated
        currentStaffDetails[raw._id] = raw;
      }
    });
    
    if (staffIdsToFetch.length > 0) {
      setLoadingStaffDetails(true);
      try {
        // Fetch staff details for unpopulated IDs
        const promises = staffIdsToFetch.map(id => 
          axiosInstance.get(`/api/personnel/${id}`)
        );
        
        const responses = await Promise.all(promises);
        responses.forEach((response, index) => {
          currentStaffDetails[staffIdsToFetch[index]] = response.data;
        });
        
        setStaffDetails(currentStaffDetails);
      } catch (error) {
        console.error('Error fetching staff details:', error);
      } finally {
        setLoadingStaffDetails(false);
      }
    } else {
      setStaffDetails(currentStaffDetails);
    }
  };

  fetchMissingStaffDetails();
}, [performance.staff, performance._id]);


  // Fetch resource data for background image
  useEffect(() => {
    const fetchResourceData = async () => {
      if (!performance._id) return;
      
      setLoadingResource(true);
      try {
        const response = await axiosInstance.get(`/api/resources/performance/${performance._id}`);
        
        if (response.data && response.data.exists !== false) {
          const resource = response.data;
          const marketingImage = getMarketingImage(resource);
          setBackgroundImage(marketingImage);
        }
      } catch (error) {
        console.log('No resource found for performance:', performance._id);
      } finally {
        setLoadingResource(false);
      }
    };

    fetchResourceData();
  }, [performance._id]);

  // Helper function to get the best marketing image for background
  const getMarketingImage = (resource: ResourceData): string | null => {
    let imageUrl: string | null = null;

    // Priority 1: Marketing assets with type 'image'
    if (resource.marketingAssets && resource.marketingAssets.length > 0) {
      const imageAsset = resource.marketingAssets.find(asset => asset.type === 'image');
      if (imageAsset) {
        imageUrl = imageAsset.url;
      }
    }

    // Priority 2: Main poster image
    if (!imageUrl && resource.mainPosterImage?.url) {
      imageUrl = resource.mainPosterImage.url;
    }

    // Priority 3: Main image
    if (!imageUrl && resource.mainImage?.url) {
      imageUrl = resource.mainImage.url;
    }

    // Priority 4: First additional promo image
    if (!imageUrl && resource.additionalPromoImages && resource.additionalPromoImages.length > 0) {
      imageUrl = resource.additionalPromoImages[0].url;
    }

    // URL encode the image URL to handle spaces and special characters
    if (imageUrl) {
      const urlParts = imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1];
      const encodedFilename = encodeURIComponent(filename);
      urlParts[urlParts.length - 1] = encodedFilename;
      return urlParts.join('/');
    }

    return null;
  };

  const eventCountdown = () => {
    if (!performance.startTime) return 'Date not set';
    const eventDate = new Date(performance.startTime);
    const today = new Date();
    eventDate.setHours(0,0,0,0);
    today.setHours(0,0,0,0);
    const daysLeft = Math.floor((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) {
      return (
        <Chip
          icon={<TheaterComedy sx={{ color: '#E57373' }} />}
          label="Event has passed. Hope it was great!"
          sx={{
            bgcolor: backgroundImage ? 'rgba(229, 115, 115, 0.95)' : 'rgba(229, 115, 115, 0.1)',
            border: '1px solid #E57373',
            borderRadius: '16px',
            px: 1,
            boxShadow: backgroundImage ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
            '& .MuiChip-label': { 
              color: backgroundImage ? 'white' : '#DAE1E7',
              fontWeight: backgroundImage ? 600 : 'normal'
            }
          }}
        />
      );
    }
    return (
      <Chip
        icon={<Schedule sx={{ color: '#3A9188' }} />}
        label={`${daysLeft} days until event`}
        sx={{
          bgcolor: backgroundImage ? 'rgba(58, 145, 136, 0.95)' : 'rgba(58, 145, 136, 0.1)',
          border: '2px solid #3A9188',
          borderRadius: '30px',
          px: 2,
          boxShadow: backgroundImage ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
          '& .MuiChip-label': {
            color: backgroundImage ? 'white' : '#DAE1E7',
            fontSize: '1rem',
            fontWeight: backgroundImage ? 600 : 'normal'
          }
        }}
      />
    );
  };

  const getArtistName = () => {
    try {
      if (performance.simpleBandName) return performance.simpleBandName;
      const band = performance.band as BandRecommendation;
      if (!band) return 'Artist TBA';
      return typeof band === 'string' ? 'Unknown Band' : (band.artistName || 'Artist TBA');
    } catch {
      return 'Artist TBA';
    }
  };

  const getVenueName = (venue?: string | Venue) => {
    try {
      if (!venue) return 'Venue TBA';
      if (typeof venue === 'string') return 'Venue details pending';
      return venue.name || 'Venue TBA';
    } catch {
      return 'Venue TBA';
    }
  };

  // New function to determine if this is a multi-day event
  const isMultiDayEvent = () => {
    if (!performance.startTime || !performance.endTime) return false;
    const startDate = new Date(performance.startTime);
    const endDate = new Date(performance.endTime);
    return !isSameDay(startDate, endDate);
  };

  // New function to get event duration
  const getEventDuration = () => {
    if (!performance.startTime || !performance.endTime) return null;
    const startDate = new Date(performance.startTime);
    const endDate = new Date(performance.endTime);
    const days = differenceInDays(endDate, startDate) + 1; // +1 to include both start and end days
    return days;
  };

  // Enhanced date formatting
  const getEventDateDisplay = () => {
    if (!performance.startTime) return 'Date TBA';
    
    const startDate = new Date(performance.startTime);
    const endDate = performance.endTime ? new Date(performance.endTime) : null;
    
    if (!endDate || isSameDay(startDate, endDate)) {
      // Single day event
      return format(startDate, 'do MMMM yyyy');
    } else {
      // Multi-day event
      const duration = getEventDuration();
      if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
        // Same month and year
        return `${format(startDate, 'do')} - ${format(endDate, 'do MMMM yyyy')} (${duration} days)`;
      } else if (startDate.getFullYear() === endDate.getFullYear()) {
        // Same year, different months
        return `${format(startDate, 'do MMMM')} - ${format(endDate, 'do MMMM yyyy')} (${duration} days)`;
      } else {
        // Different years
        return `${format(startDate, 'do MMMM yyyy')} - ${format(endDate, 'do MMMM yyyy')} (${duration} days)`;
      }
    }
  };

  const getEventInfo = () => {
    try {
      // Strand info
      let strandJSX = null;
      if (performance.strandId) {
        if (typeof performance.strandId === 'string') {
          strandJSX = (
            <Typography variant="subtitle1" sx={{ 
              display:'flex', 
              alignItems:'center', 
              mb:1,
              ...(backgroundImage && {
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
              })
            }}>
              <CalendarMonth sx={{ mr:1, color:'primary.main' }} />
              Strand ID: {performance.strandId}
            </Typography>
          );
        } else {
          strandJSX = (
            <>
              <Typography variant="subtitle1" sx={{ 
                display:'flex', 
                alignItems:'center', 
                mb:1,
                ...(backgroundImage && {
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
                })
              }}>
                <CalendarMonth sx={{ mr:1, color:'primary.main' }} />
                Strand: {performance.strandId.name || 'Name pending'}
              </Typography>
            </>
          );
        }
      }

      // Event + Venue or top-level Venue
      let locationJSX = null;
      if (performance.event && typeof performance.event !== 'string') {
        locationJSX = (
          <>  
            <Typography variant="subtitle1" sx={{ 
              display:'flex', 
              alignItems:'center', 
              mb:1,
              ...(backgroundImage && {
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
              })
            }}>
              <EventIcon sx={{ mr:1, color:'primary.main' }} />
              Event: {performance.event.name || 'Name pending'}
            </Typography>
            <Typography variant="subtitle1" sx={{ 
              display:'flex', 
              alignItems:'center', 
              mb:1,
              ...(backgroundImage && {
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
              })
            }}>
              <LocationOn sx={{ mr:1, color:'primary.main' }} />
              Venue: {getVenueName(performance.event.venue as Venue)}
            </Typography>
          </>
        );
      } else if ((performance.venue as Venue) || typeof performance.venue === 'object') {
        locationJSX = (
          <Typography variant="subtitle1" sx={{ 
            display:'flex', 
            alignItems:'center', 
            mb:1,
            ...(backgroundImage && {
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
            })
          }}>
            <LocationOn sx={{ mr:1, color:'primary.main' }} />
            Venue: {getVenueName(performance.venue as Venue)}
          </Typography>
        );
      }

      return (
        <>
          {strandJSX}
          {locationJSX}
        </>
      );
    } catch {
      return <Alert severity="warning">Event information unavailable</Alert>;
    }
  };

  const formatTime = (time?: Date | string) => {
    try {
      if (!time) return 'TBA';
      const d = time instanceof Date ? time : new Date(time);
      if (isNaN(d.getTime())) return 'Invalid date';
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'TBA';
    }
  };

  // Enhanced time display for multi-day events
  const formatDateTime = (time?: Date | string) => {
    try {
      if (!time) return 'TBA';
      const d = time instanceof Date ? time : new Date(time);
      if (isNaN(d.getTime())) return 'Invalid date';
      
      if (isMultiDayEvent()) {
        // For multi-day events, show both date and time
        return {
          date: format(d, 'MMM do'),
          time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
      } else {
        // For single-day events, show only time
        return {
          date: null,
          time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
      }
    } catch {
      return { date: null, time: 'TBA' };
    }
  };

 const renderStaff = () => {
  if (!performance.staff?.length) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        color: backgroundImage ? 'rgba(255,255,255,0.8)' : '#A4B0BE', 
        gap: 1 
      }}>
        <Groups sx={{ color: '#3A9188' }} />
        <Typography sx={{
          ...(backgroundImage && {
            textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
          })
        }}>
          No staff assigned yet
        </Typography>
      </Box>
    );
  }

  if (loadingStaffDetails) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} sx={{ color: '#3A9188' }} />
        <Typography sx={{ color: backgroundImage ? 'rgba(255,255,255,0.8)' : '#A4B0BE' }}>
          Loading staff details...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {performance.staff.map((assignment: any) => {
        try {
          const raw = assignment.staffId;
          let staffData;
          
          if (typeof raw === 'object' && raw._id) {
            // Already populated
            staffData = {
              _id: raw._id,
              name: raw.name || 'Name not available',
              role: assignment.assignedRole || 'Role pending'
            };
          } else if (typeof raw === 'string') {
            // Use fetched details
            const fetchedData = staffDetails[raw];
            staffData = {
              _id: raw,
              name: fetchedData?.name || 'Loading...',
              role: assignment.assignedRole || 'Role pending'
            };
          } else {
            // Fallback
            staffData = {
              _id: 'unknown',
              name: 'Name not available',
              role: assignment.assignedRole || 'Role pending'
            };
          }

          const baseIcon = roleIcons[staffData.role] || <Person />;
          const roleIcon = React.cloneElement(baseIcon, { sx: { fontSize: 32, color: '#3A9188' } });

          return (
            <Box
              key={staffData._id.toString()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                bgcolor: backgroundImage ? 'rgba(52, 73, 94, 0.95)' : 'rgba(52, 73, 94, 0.8)',
                borderRadius: 1,
                border: '1px solid rgba(58, 145, 136, 0.1)',
                boxShadow: backgroundImage ? '0 2px 8px rgba(0,0,0,0.3)' : 'none'
              }}
            >
              <Avatar
                sx={{
                  bgcolor: 'rgba(58, 145, 136, 0.2)',
                  border: '1px solid #3A9188',
                  width: 50,
                  height: 50
                }}
              >
                {roleIcon}
              </Avatar>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ 
                  color: '#fff', 
                  fontWeight: 500,
                  ...(backgroundImage && {
                    textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
                  })
                }}>
                  {staffData.name}
                </Typography>
                <Typography sx={{ 
                  color: backgroundImage ? 'rgba(255,255,255,0.8)' : '#A4B0BE', 
                  fontStyle: 'italic',
                  ...(backgroundImage && {
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                  })
                }}>
                  {staffData.role}
                </Typography>
              </Box>
            </Box>
          );
        } catch {
          return null;
        }
      })}
    </Box>
  );
};

  if (!performance) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">No performance data available</Alert>
        </CardContent>
      </Card>
    );
  }

  const startDateTime = formatDateTime(performance.startTime);
  const endDateTime = formatDateTime(performance.endTime);
  const multiDay = isMultiDayEvent();

  return (
    <Card sx={{ 
      mb: 4, 
      backgroundColor: '#34495E', 
      border: '1px solid #3A9188',
      position: 'relative',
      overflow: 'hidden',
      // Background image styling
      ...(backgroundImage && {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      })
    }}>
      {/* Background overlay when there's a background image */}
      {backgroundImage && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(52,73,94,0.9) 0%, rgba(52,73,94,0.7) 50%, rgba(52,73,94,0.8) 100%)',
            zIndex: 1
          }}
        />
      )}
      
      <CardContent sx={{ position: 'relative', zIndex: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h1" sx={{ 
            color: '#fff', 
            fontWeight: 700,
            ...(backgroundImage && {
              textShadow: '3px 3px 6px rgba(0,0,0,0.9)'
            })
          }}>
            {getArtistName()}
          </Typography>
          {eventCountdown()}
        </Box>

        {/* Enhanced date display */}
        <Typography variant="h3" sx={{ 
          color: '#E57373', 
          fontWeight: 500, 
          fontSize: '1.75rem', 
          mb: 3,
          ...(backgroundImage && {
            textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
          })
        }}>
          {getEventDateDisplay()}
        </Typography>

        {/* Multi-day indicator */}
        {multiDay && (
          <Box sx={{ mb: 2 }}>
            <Chip
              icon={<DateRange sx={{ color: '#3A9188' }} />}
              label={`${getEventDuration()}-Day Event`}
              sx={{
                bgcolor: backgroundImage ? 'rgba(58, 145, 136, 0.95)' : 'rgba(58, 145, 136, 0.2)',
                border: '1px solid #3A9188',
                color: backgroundImage ? 'white' : '#DAE1E7',
                boxShadow: backgroundImage ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
                fontWeight: backgroundImage ? 600 : 'normal'
              }}
            />
          </Box>
        )}

        {/* Strand & Venue Info */}
        <Paper sx={{ 
          bgcolor: backgroundImage ? 'rgba(52, 73, 94, 0.9)' : 'rgba(52, 73, 94, 0.6)', 
          p: 2, 
          mb: 3, 
          borderRadius: 2, 
          border: '1px solid rgba(58,145,136,0.2)',
          boxShadow: backgroundImage ? '0 2px 8px rgba(0,0,0,0.3)' : 'none'
        }}>
          {getEventInfo()}
        </Paper>

        {/* Enhanced Time Section */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
          {/* Start Time */}
          <Paper sx={{ 
            bgcolor: backgroundImage ? 'rgba(52, 73, 94, 0.9)' : 'rgba(52, 73, 94, 0.6)', 
            p: 2, 
            borderRadius: 2, 
            border: '1px solid rgba(58,145,136,0.2)',
            boxShadow: backgroundImage ? '0 2px 8px rgba(0,0,0,0.3)' : 'none'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Timer sx={{ color: '#3A9188' }} />
              <Box>
                <Typography variant="overline" sx={{ 
                  color: backgroundImage ? 'rgba(255,255,255,0.8)' : '#A4B0BE',
                  ...(backgroundImage && {
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                  })
                }}>
                  {multiDay ? 'START' : 'START TIME'}
                </Typography>
                {multiDay && typeof startDateTime === 'object' && startDateTime.date && (
                  <Typography variant="body2" sx={{ 
                    color: backgroundImage ? 'rgba(255,255,255,0.8)' : '#A4B0BE',
                    ...(backgroundImage && {
                      textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                    })
                  }}>
                    {startDateTime.date}
                  </Typography>
                )}
                <Typography variant="h5" sx={{ 
                  color: '#fff',
                  ...(backgroundImage && {
                    textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
                  })
                }}>
                  {typeof startDateTime === 'object' && startDateTime.time
                    ? startDateTime.time
                    : typeof startDateTime === 'string'
                      ? startDateTime
                      : 'TBA'}
                </Typography>
              </Box>
            </Box>
          </Paper>
          
          {/* End Time */}
          <Paper sx={{ 
            bgcolor: backgroundImage ? 'rgba(52, 73, 94, 0.9)' : 'rgba(52, 73, 94, 0.6)', 
            p: 2, 
            borderRadius: 2, 
            border: '1px solid rgba(229,115,115,0.2)',
            boxShadow: backgroundImage ? '0 2px 8px rgba(0,0,0,0.3)' : 'none'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Timer sx={{ color: '#E57373' }} />
              <Box>
                <Typography variant="overline" sx={{ 
                  color: backgroundImage ? 'rgba(255,255,255,0.8)' : '#A4B0BE',
                  ...(backgroundImage && {
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                  })
                }}>
                  {multiDay ? 'END' : 'END TIME'}
                </Typography>
                {multiDay && typeof endDateTime === 'object' && endDateTime.date && (
                  <Typography variant="body2" sx={{ 
                    color: backgroundImage ? 'rgba(255,255,255,0.8)' : '#A4B0BE',
                    ...(backgroundImage && {
                      textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                    })
                  }}>
                    {endDateTime.date}
                  </Typography>
                )}
                <Typography variant="h5" sx={{ 
                  color: '#fff',
                  ...(backgroundImage && {
                    textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
                  })
                }}>
                  {typeof endDateTime === 'object' && endDateTime.time
                    ? endDateTime.time
                    : typeof endDateTime === 'string'
                      ? endDateTime
                      : 'TBA'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Staff Section */}
        <Paper sx={{ 
          bgcolor: backgroundImage ? 'rgba(52, 73, 94, 0.9)' : 'rgba(52, 73, 94, 0.6)', 
          p: 3, 
          borderRadius: 2, 
          border: '1px solid rgba(58,145,136,0.2)',
          boxShadow: backgroundImage ? '0 2px 8px rgba(0,0,0,0.3)' : 'none'
        }}>
          <Typography variant="h6" sx={{ 
            color: '#3A9188', 
            mb: 2, 
            fontWeight: 600, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            ...(backgroundImage && {
              textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
            })
          }}>
            <TheaterComedy /> Assigned Staff
          </Typography>
          {renderStaff()}
        </Paper>
      </CardContent>
    </Card>
  );
};

export default BandEventInfo;